function PrivacyPolicy() {
    return (
        <div className="privacy-policy">
            <h1>
                Privacy Policy
            </h1>
            <h4>Effective Date: December 12, 2023</h4>
            <h3>
                1. Introduction
            </h3>
            <p>
                This Privacy Policy describes how Voyager ("Voyager", "we", "us", or "our") collects, uses, and discloses your information when you use our mobile application ("App"). We are committed to protecting your privacy and ensuring that your information is handled responsibly.
            </p>
            <h4>App Information:</h4>
                <dl>
                    <dt>App Name:</dt> <dd>Voyager</dd>
                    <dt>App Description:</dt> <dd>Location-based alert system</dd>
                    <dt>App Functionality:</dt> <dd>Alerts the user based on their location when they reach a specific radius around a target location</dd>
                    <dt>Target Audience:</dt> <dd>Users above 13 years old</dd>
                    <dt>Supported Platforms:</dt> <dd>Android</dd>
                    <dt>Available Regions:</dt> <dd>Worldwide</dd>
                    <dt>Data Collection Practices:</dt><dd>
                        * We collect email addresses to create user accounts and identify users within the app.<br />
                        * We collect location data (including GPS coordinates) to determine proximity to target locations and trigger alerts. This location data used only within the app it will not uploaded, or shared to any third parties.<br />
                        * We do not share any user data with third parties.<br />
                    </dd>

                    <dt>Data Usage:</dt><dd>
                        * Email for account management and communication<br/>
                        * Location data for triggering alerts<br/>
                        * Usage data for app improvement and analytics</dd>

                    <dt>Data Sharing:</dt><dd>
                        * No personally identifiable information is shared with third parties<br/>
                        * Non-personally identifiable usage data may be shared with third-party analytics providers
                    </dd>

                    <dt>User Rights:</dt><dd>
                        * Access and update account information<br/>
                        * Opt-out of location data collection<br/>
                        * Delete user account</dd>

                    <dt>Security:</dt><dd>
                        * Standard industry measures are implemented to protect user data<br/>
                    </dd>

                    <dt>Children's Privacy:</dt><dd>
                        * App is not directed towards children<br/>
                        * Parental consent is required for users under 16 (applicable for EU)<br/>
                    </dd>

                    <dt>Data Retention:</dt><dd>
                        * User data is retained for as long as necessary for app functionalities<br/>
                        * Specific retention periods for different data types (e.g., email, location data)<br/>
                    </dd>
                    <dt>Legal Compliance:</dt><dd>
                        * App strives to comply with all applicable data protection regulations<br/>
                        * Specific compliance information for major regions (e.g., GDPR, CCPA)<br/>
                    </dd>


                </dl>
            <p>
                Please read this Privacy Policy carefully before using our App. By accessing or using the App, you agree to be bound by this Privacy Policy.
            </p>
            <h3>2. Information We Collect</h3>
            <p>
                We collect three main types of information when you use our App:
            </p>
            <h4>
                2.1 Email
            </h4>
            <ul>
                <li>We collect your email address when you create an account. This allows us to identify you and send you important information about your account, such as updates, support notifications, and changes to this Privacy Policy.</li>
                <li>We use a universal user unique identifier generated from your email address to identify you within the app. This identifier does not contain any personally identifiable information.</li>
                <li>We will never share your email address with any third-parties.</li>
            </ul>
            <h4>
                2.2 Location Data
            </h4>
            <ul>
                <li>
                    We collect your location data (including GPS coordinates) to determine your proximity to target locations and trigger alerts.
                </li>
                <li>
                    Your location data is only collected while the App is running and actively being used.
                </li>
                <li>
                    We do not store your location data for any other purposes, and it is not shared with any third-parties.
                </li>
                <li>
                    You can disable location data collection for the App at any time through your device settings.
                </li>
            </ul>
            <h4>
                2.3 Usage Data
            </h4>
            <ul>
                <li>
                    We collect non-personally identifiable usage data to help us improve the App and provide a better user experience.
                </li>
                <li>
                    This data includes information such as the date and time you use the App, the features you use, and any errors you encounter.
                </li>
                <li>
                    We use this data to analyze usage patterns, identify bugs, and improve the overall functionality of the App.
                </li>
                <li>
                    We may share anonymous and aggregated usage data with third-party analytics providers. However, this data will not include any information that could be used to identify you.
                </li>
            </ul>
            <h3>
                3. How We Use Your Information
            </h3>
            <p>
                We use the information we collect from you for the following purposes:
            </p>
            <h4>
                3.1 Email
            </h4>
            <ul>
                <li>
                    To create and manage your user account
                </li>
                <li>
                    To send you important information about the App, such as updates, support notifications, and changes to this Privacy Policy
                </li>
                <li>
                    To respond to your inquiries and requests
                </li>
                <li>
                    To personalize your experience with the App
                </li>
            </ul>
            <h4>
                3.2 Location Data
            </h4>
            <ul>
                <li>
                    To determine your proximity to target locations and trigger location-based alerts
                </li>
                <li>
                    To improve the accuracy of the App's location services
                </li>
            </ul>
            <h4>
                3.3 Usage Data
            </h4>
            <ul>
                <li>
                    To analyze usage patterns and identify areas for improvement
                </li>
                <li>
                    To troubleshoot and fix bugs
                </li>
                <li>
                    To develop new features and services
                </li>
                <li>
                    To personalize your experience with the App
                </li>
                <li>
                    To measure the effectiveness of marketing campaigns
                </li>
                <li>
                    To comply with legal and regulatory requirements
                </li>
            </ul>
            <p>
                We will only use your information for the purposes described in this Privacy Policy. We will not use your information for any other purposes without your prior consent.
            </p>
            <h3>4. How We Share Your Information</h3>
            <h4>
                4.1 Sharing with Third Parties
            </h4>
            <p>
                We do not share any personally identifiable information (such as your email address) with third-parties without your consent. However, we may share non-personally identifiable information with third-party analytics providers to help us improve the App. This data will be aggregated and anonymized and will not contain any information that could be used to identify you.
            </p>
            <h4>
                4.2 International Transfers
            </h4>
            <p>
                Your information may be transferred to and processed in countries other than your own, including the United States. These countries may have different data protection laws than your country. However, we will take all necessary steps to ensure that your information is treated securely and in accordance with this Privacy Policy.
                <br /><br />
                We use standard contractual clauses approved by the European Commission to protect your personal information when it is transferred to countries outside the European Economic Area. These clauses ensure that your personal information is treated with the same level of protection as it would be in the European Economic Area.
                <br /><br />
                By using our App, you consent to the transfer, processing, and storage of your information in accordance with this Privacy Policy.
            </p>

            <h3>5. Your Choices and Rights</h3>
            <h4>5.1 Access and Update Your Information</h4>
            <p>
                You can access and update your email address and other account information through the App settings.
            </p>
            <h4>
                5.2 Opt-Out of Location Data Collection
            </h4>
            <p>
                You can disable location data collection for the App at any time through your device settings. However, this will prevent the App from functioning properly as it requires your location data to trigger alerts.
            </p>
            <h4>
                5.3 Delete Your Account
            </h4>
            <p>
                You can request to delete your account and all associated data by contacting us at <a href="mailto:shiva.v2232@gmail.com">shiva.v2232@gmail.com</a>. Once your account is deleted, we will no longer be able to provide you with access to the App or its features.
            </p>
            <h4>
                5.4 Your Rights Under Applicable Data Protection Regulations
            </h4>
            <p>
                Depending on your location, you may have additional rights under applicable data protection regulations, such as:
            </p>
            <ul>
                <li>The right to access your personal information. You can request a copy of the personal information we hold about you.</li>
                <li>The right to rectify your personal information. You can request that we correct any inaccurate or incomplete personal information we hold about you.</li>
                <li>The right to erase your personal information. You can request that we delete your personal information under certain circumstances.</li>
                <li>The right to restrict the processing of your personal information. You can request that we restrict the processing of your personal information under certain circumstances.</li>
                <li>The right to object to the processing of your personal information. You can object to the processing of your personal information for certain purposes.</li>
                <li>The right to data portability. You can request that we transfer your personal information to another data controller.</li>
            </ul>
            <p>
                To exercise any of these rights, please contact us at <a href="mailto:shiva.v2232@gmail.com">shiva.v2232@gmail.com</a>.
                <br /><br />
                Please note that these rights may be subject to limitations or restrictions under applicable data protection regulations.
            </p>

            <h3>6. Data Retention</h3>
            <p>We retain your information for as long as it is necessary to fulfill the purposes outlined in this Privacy Policy. We may also retain your information for longer periods as required by law, to resolve disputes, prevent fraud, or enforce our agreements.
                <br /><br />
                Here are the specific data retention periods for the different types of information we collect:
            </p>
            <h4>6.1 Email Address:</h4>
            <ul>
                <li>We will retain your email address for as long as your account is active.</li>
                <li>If you delete your account, we will delete your email address within a reasonable timeframe.</li>
            </ul>

            <h4>6.2 Location Data:</h4>

            <ul>
                <li>We only store your location data while the App is running(either foreground or background) and actively being used.</li>
                <li>Once you close the App, your GPS location data is immediately deleted.</li>
                <li>We do not store any historical location data.</li>
            </ul>
            <h4>6.3 Usage Data:</h4>
            <ul>
                <li>We retain usage data for a limited period of time, typically up to 2 years.</li>
                <li>After this period, we either delete the data or aggregate and anonymize it so that it can no longer be used to identify you.</li>
            </ul>
            <p>
                We will notify you if we need to retain your information for a longer period than outlined in this Privacy Policy. You can also request that we delete your information at any time by contacting us at <a href="mailto:shiva.v2232@gmail.com">shiva.v2232@gmail.com</a>.
            </p>

            <h3>7. Security</h3>
            <p>We take reasonable measures to protect your information from unauthorized access, use, disclosure, alteration, or destruction. These measures include:</p>
            <ul>
                <li>Using industry-standard encryption to protect your information in transit and at rest.</li>
                <li>Implementing access controls to ensure that only authorized personnel have access to your information.</li>
                <li>Regularly testing and updating our security systems and procedures.</li>
                <li>Monitoring for suspicious activity and taking appropriate action to address any potential threats.</li>
            </ul>
            <p>
                However, no method of transmission over the internet or method of electronic storage is 100% secure. Therefore, we cannot guarantee absolute security of your information.
                <br /><br />
                If you believe that your information has been compromised, please contact us immediately at <a href="mailto:shiva.v2232@gmail.com">shiva.v2232@gmail.com</a>.
            </p>
            <h3>8. Children's Privacy</h3>
            <p>
                We do not knowingly collect or solicit personal information from children under the age of 13. If you are a parent or guardian and you believe that your child has provided us with personal information, please contact us immediately at <a href="mailto:shiva.v2232@gmail.com">shiva.v2232@gmail.com</a>. We will delete any personal information collected from children under the age of 13 without verification of parental consent.
                <br /><br />
                If you are located in the European Economic Area, we require verifiable parental consent for any user under the age of 16. This means that we will require a parent or guardian to provide their consent before we collect or use any personal information from a user under the age of 16.
            </p>
            <h3>9. Changes to this Privacy Policy</h3>
            <p>
                We may update this Privacy Policy from time to time to reflect changes in our data collection practices, legal requirements, or other factors. We encourage you to review the Privacy Policy regularly for the latest information on our privacy practices.
                <br /><br />
                If we make any material changes to this Privacy Policy, we will notify you by posting the updated Privacy Policy on our website and within the App. We will also notify you via email if you have provided us with your email address.
                <br /><br />
                Your continued use of the App after the effective date of any changes constitutes your acceptance of the revised Privacy Policy.
            </p>
            <h3>10. Contact Us</h3>
            <p>If you have any questions about this Privacy Policy or our data collection practices, please contact us at:</p>
            <dl>
                <dt>Email:</dt><dd><a href="mailto:shiva.v2232@gmail.com">shiva.v2232@gmail.com</a></dd>
            </dl>
                <center><p>We will do our best to respond to your inquiries promptly.</p></center>
            
        </div>
    );
}

export default PrivacyPolicy;